import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, RotateCcw } from "lucide-react";
import Canvas from "./Canvas";
import api, { getToken, setToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

function formatError(err) {
  if (!err) return "Unknown error";
  if (err.response) {
    const s = err.response.status;
    if (s === 401) return "Wrong password";
    if (s === 403) return "Blocked by the firewall. Clear cookies and try again.";
    if (s >= 500) return `Server error (${s}). Try again in a minute.`;
    return `HTTP ${s}: ${err.response.data?.detail || "Request rejected"}`;
  }
  if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
    return "Can't reach the server. Check your internet.";
  }
  if (err.code === "ECONNABORTED") return "Request timed out.";
  return err.message || "Login failed";
}

async function loginWithRetry(password, attempts = 3) {
  let last;
  for (let i = 0; i < attempts; i++) {
    try {
      return await api.auth.login(password);
    } catch (err) {
      last = err;
      // Don't retry a 401 — the password is wrong.
      if (err?.response?.status === 401) throw err;
      await new Promise((r) => setTimeout(r, 500 * Math.pow(2, i)));
    }
  }
  throw last;
}

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // On mount, validate any existing token with the server.
  useEffect(() => {
    let alive = true;
    const t = getToken();
    if (!t) {
      setChecking(false);
      return;
    }
    api.auth
      .me()
      .then(() => alive && setAuthed(true))
      .catch(() => setToken(null))
      .finally(() => alive && setChecking(false));
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErrMsg("");
    try {
      const { token } = await loginWithRetry(pw);
      setToken(token);
      setAuthed(true);
      toast("Welcome back. Edit mode enabled.");
    } catch (err) {
      const msg = formatError(err);
      setErrMsg(msg);
      toast(msg);
      // eslint-disable-next-line no-console
      console.error("Login error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    setToken(null);
    setAuthed(false);
    navigate("/");
  };

  const hardReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      // best-effort cookie clearing for the current domain
      document.cookie.split(";").forEach((c) => {
        const eq = c.indexOf("=");
        const name = (eq > -1 ? c.slice(0, eq) : c).trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
    } catch (e) {
      // ignore
    }
    toast("Local data cleared — reloading…");
    setTimeout(() => window.location.reload(true), 400);
  };

  if (checking) {
    return (
      <div className="min-h-screen w-screen paper-grain flex items-center justify-center">
        <div className="font-display text-2xl text-ink-soft/40 animate-pulse">
          Verifying session…
        </div>
      </div>
    );
  }

  if (authed) return <Canvas mode="edit" onLogout={logout} />;

  return (
    <div className="min-h-screen w-screen paper-grain flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="font-display text-5xl leading-none mb-2">STUDIO ACCESS</div>
        <p className="font-serif-i text-ink-soft mb-8">
          Private door for the maker only.
        </p>
        <form
          onSubmit={submit}
          className="bg-white rounded-xl border border-[var(--ink)]/10 card-stack p-6"
        >
          <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft flex items-center gap-2">
            <Lock className="w-3 h-3" /> Password
          </label>
          <Input
            type="password"
            value={pw}
            onChange={(e) => {
              setPw(e.target.value);
              setErrMsg("");
            }}
            placeholder="••••••••"
            className="mt-2 h-12 text-lg"
            autoFocus
          />
          {errMsg && (
            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {errMsg}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting || !pw}
            className="w-full mt-4 h-11 bg-[var(--vermillion)] hover:bg-[var(--vermillion-soft)] text-white btn-pop"
          >
            {submitting ? "Entering…" : "Enter studio"}{" "}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </form>

        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-[var(--vermillion)]"
          >
            ← Back to public canvas
          </button>
          <button
            onClick={hardReset}
            className="text-[11px] uppercase tracking-[0.18em] text-ink-soft/70 hover:text-[var(--vermillion)] flex items-center gap-1"
            title="Wipes local/session storage & cookies for this site, then reloads"
          >
            <RotateCcw className="w-3 h-3" />
            Reset local data
          </button>
        </div>
      </div>
    </div>
  );
}
